import { Box, LinearProgress, Typography } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { Phase, PhaseType, PlanConfig } from '../types';
import { Pill } from './Pill';

interface PhasedPlanTabProps {
  phases: Phase[];
  config: PlanConfig;
}

const typePill: Record<PhaseType, { color: string; bg: string }> = {
  warmup: { color: PLAN_COLORS.past, bg: PLAN_COLORS.pastBg },
  ramp: { color: PLAN_COLORS.newBatch, bg: PLAN_COLORS.amberBg },
  scale: { color: PLAN_COLORS.newBatch, bg: PLAN_COLORS.amberBg },
  steady: { color: PLAN_COLORS.green, bg: PLAN_COLORS.greenBg },
};

const barColor: Record<PhaseType, string> = {
  warmup: PLAN_COLORS.past,
  ramp: PLAN_COLORS.newBatch,
  scale: PLAN_COLORS.newBatch,
  steady: PLAN_COLORS.green,
};

const Cell = ({ label, value, sub }: { label: string; value: string; sub: string }) => (
  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500, mb: 0.375 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace' }}>{value}</Typography>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>
  </Box>
);

export const PhasedPlanTab = ({ phases, config }: PhasedPlanTabProps) => (
  <Box>
    {phases.map((p) => {
      const capRange =
        p.capStart === p.capEnd
          ? p.capEnd.toLocaleString()
          : `${p.capStart.toLocaleString()} → ${p.capEnd.toLocaleString()}`;
      return (
        <Box
          key={p.label}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 2,
            mb: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.label}</Typography>
            <Pill color={typePill[p.type].color} bg={typePill[p.type].bg}>
              {p.type}
            </Pill>
            <Pill color="text.secondary" bg="grey.200">
              batch every {p.cadence}d
            </Pill>
            {p.hasNew && (
              <Pill color={PLAN_COLORS.both} bg={PLAN_COLORS.bothBg}>
                + new leads from day {config.newStart}
              </Pill>
            )}
            <Typography
              sx={{
                ml: 'auto',
                fontSize: 11,
                color: 'text.secondary',
                fontFamily: 'monospace',
              }}
            >
              {p.freqNote}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.25,
              mb: 1.25,
            }}
          >
            <Cell label="Send capacity" value={capRange} sub="emails/day" />
            <Cell label="Active leads" value={p.active.toLocaleString()} sub={`${p.pct}% of pool`} />
            <Cell label="Emails/day needed" value={p.needed.toLocaleString()} sub="at steady freq" />
            <Cell
              label="Addresses · leads/batch"
              value={`${p.addrs} · ${p.bsize.toLocaleString()}`}
              sub="per batch"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={p.pct}
            sx={{
              height: 5,
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: barColor[p.type] },
            }}
          />
        </Box>
      );
    })}
  </Box>
);
